import React from 'react'

import * as c from '../../../../common'
import * as r from '../../../../react-utils'

import * as mi from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'

import * as cp from '../../../comps'
import * as cpm from '../../../../app/components'

import Cost, { ICost } from './Cost'

//@ts-ignore
import styles from './Summary.module.css'

type NirvanaSummaryProps = c.r<{
  children?: never
  isPDF: boolean
  noPDF: boolean
}>

export default React.memo<NirvanaSummaryProps>(function NirvanaSummary({
  isPDF,
  noPDF,
}) {
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `customerID route param not found in <NirvanaSummary />, instead found: ${customerID}`,
    )
  }
  //#region styling
  const nativePrefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const innerTheme = React.useMemo(
    (): Theme =>
      createTheme({
        breakpoints: { values: r.MUIBreakpoints },
        palette: { mode: nativePrefersDark ? 'dark' : 'light' },
      }),
    [nativePrefersDark],
  )
  //#endregion
  //#region global
  const [customerData] = r.useCustomer(customerID)
  const { currProducts } = r.useProducts(customerID)
  const totalCustomerCost = r.useTotalCost('customer', customerID, !isPDF)
  //#endregion
  //#region local
  const [closerCalcOpen, , toggleCloserCalc] = r.useBool(false)
  const [, , payMethodToggle] = r.useSessionVal('payMethodOpen')

  const [editingEfficiency, setEditingEfficiency] = React.useState('')
  const handleCloseEfficiencyEditor = React.useCallback(() => {
    setEditingEfficiency('')
  }, [setEditingEfficiency])
  //#endregion

  const costs = React.useMemo((): ICost[] => {
    const fieldsWithCustomerCost = c.getAddersWithCustomerCost(customerData)

    return fieldsWithCustomerCost.map((field) => {
      return {
        customer: customerData,
        name: field,
        selected: customerData[(field + '_show') as c.CustomerField] === 1,
        value: customerData[
          (field + '_customer_cost') as c.CustomerField
        ] as string,
      }
    })
  }, [customerData])

  const colorTotalStyle = React.useMemo((): React.CSSProperties => {
    return {
      backgroundColor: c.companyColors[customerData.solarCompany],
    }
  }, [customerData.solarCompany])

  return (
    <>
      <cp.PageReport
        autoHeight
        company={customerData.solarCompany}
        companyBorder
        hPad
        isPDF={isPDF}
        noPDF={noPDF}
        title="Job Summary"
      >
        <table className={styles['table-root']}>
          <tbody>
            {costs.map((cost) => (
              <Cost
                customer={customerData}
                customerID={customerID}
                isPDF={isPDF}
                key={cost.name}
                name={cost.name}
                onEfficiencyEdit={setEditingEfficiency}
                value={cost.value}
              />
            ))}
          </tbody>
        </table>

        <div className={styles['check-total-cost']} style={colorTotalStyle}>
          <span className={styles['check-total-cost-label']}>
            <span>Total:</span>
          </span>
          <span className={styles['check-total-cost-price']}>
            <span>${totalCustomerCost.toFixed(2)}</span>
          </span>
        </div>

        {!isPDF && (
          <div className={styles['calc-buttons']}>
            {(currProducts.includes('Solar') || currProducts.length === 0) &&
              c.CODENAME !== 'nirvana' && (
                <>
                  {/* Dunno even why */}
                  {/* @ts-expect-error */}
                  <mi.Calculate
                    //
                    onClick={toggleCloserCalc}
                    size={24}
                  />
                </>
              )}

            {(currProducts.includes('HIL') ||
              currProducts.includes('Security') ||
              currProducts.length === 0) && (
              <>
                {/* Dunno even why */}
                {/* @ts-expect-error */}
                <mi.CandlestickChart
                  //
                  onClick={payMethodToggle}
                  size={24}
                />
              </>
            )}
          </div>
        )}

        {!!customerData.globalNotes.trim() && (
          <div className={styles['global-notes']}>
            {customerData.globalNotes
              .trim()
              .split('\n')
              .map((noteLine, i) => (
                <p className={styles['global-note-line']} key={i}>
                  {noteLine}
                </p>
              ))}
          </div>
        )}
        <cp.Pad amt={20} />
        <p className={styles['efficiency-note']}>
          This is an agreement that all work described on this report will be
          completed.
        </p>
        <p className={styles['efficiency-note']}>
          Customer understands the timeline in which jobs will be completed as
          explained by the representative.
        </p>
      </cp.PageReport>

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog
          noCanvas
          onClose={handleCloseEfficiencyEditor}
          open={!!editingEfficiency}
        >
          {!!editingEfficiency && (
            <cpm.CheckGroup
              as="canvas"
              customerID={customerID}
              field={editingEfficiency as c.CustomerField}
            />
          )}
        </cp.Dialog>
      </ThemeProvider>

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog onClose={toggleCloserCalc} open={closerCalcOpen}>
          <cpm.CloserCalc customerID={customerID} deductDisabled />
        </cp.Dialog>
      </ThemeProvider>
    </>
  )
})
